
import * as React from "react";
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import i18next from 'i18next';
import  { useEffect } from 'react';
import 'react-calendar/dist/Calendar.css';
import 'react-datepicker/dist/react-datepicker.css';
import { ErrorBoundary } from 'react-error-boundary';
import { I18nextProvider } from 'react-i18next';
import 'react-perfect-scrollbar/dist/css/styles.css';
import 'react-phone-input-2/lib/style.css';
import { BrowserRouter } from 'react-router-dom';
import AppSnackBar from './Components/AppSnackbar';
import MainLayout from './Containers/MainLayout';
import AppBootLoader from './GlobalComponents/AppBootLoader';
import { AppContextProvider } from './Helpers/Contexts/appContext';
import { AuthContextProvider } from './Helpers/Contexts/authContext';
import { FormContextProvider } from './Helpers/Contexts/formContext';
import { SearchContextProvider } from './Helpers/Contexts/searchContext';
import { EventContextProvider } from './Helpers/Contexts/eventContext';
import ErrorFallback from './Helpers/ErrorFallback';
import GlobalThemeProvider from './Helpers/Themes/themeProvider';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { FileUploadContext } from './Containers/VenuePortal/src/contexts/FileUploadContext';
import { IonApp } from '@ionic/react';
import { setupIonicReact } from '@ionic/react';
import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';
import storageService from './Utils/storageService';
import NativeLaunchAnimation from './GlobalComponents/NativeLaunchAnimation';

/* Import Global Styles including General Sans font */
import './theme/variables.css';

setupIonicReact();

async function configureNativeChrome() {
  if (!Capacitor.isNativePlatform()) return;
  try {
    document.documentElement.classList.add('is-native-app');
    // Overlay so safe-area insets are applied; CSS pads headers clear of status bar
    await StatusBar.setOverlaysWebView({ overlay: true });
    // Style.Light = "dark text/icons for a light background" (Capacitor names styles after
    // the background they suit, not the icon color). Style.Dark was making Android's status
    // bar icons render white-on-white against our white header, effectively invisible.
    await StatusBar.setStyle({ style: Style.Light });
    await StatusBar.setBackgroundColor({ color: '#ffffff' });
  } catch (err) {
    console.warn('StatusBar setup failed', err);
  }
}

configureNativeChrome();

/** Keep N-Genius / capture / portal navigations inside the Capacitor WebView */
function keepPaymentFlowInApp() {
  if (!Capacitor.isNativePlatform()) return;

  const inAppHosts = [
    "ngenius-payments.com",
    "thekenverse.com",
    "ken42.com",
    "network.ae",
  ];

  const shouldStayInApp = (url: string) => {
    try {
      const host = new URL(url, window.location.href).hostname;
      return inAppHosts.some((h) => host === h || host.endsWith(`.${h}`));
    } catch {
      return false;
    }
  };

  const originalOpen = window.open.bind(window);
  window.open = (url?: string | URL, target?: string, features?: string) => {
    const href = url ? String(url) : "";
    if (href && shouldStayInApp(href)) {
      // These pages (N-Genius, portal capture) are outside our control and don't add
      // their own iOS safe-area padding. Our own status bar overlays the WebView so
      // *our* CSS can push content below it — a page we don't control has no reason to
      // do the same, so it rendered edge-to-edge with its header behind the notch.
      // Disabling the overlay first makes iOS reserve real space for the status bar
      // instead, giving the external page a normal top inset with no cooperation needed.
      (async () => {
        if (Capacitor.isNativePlatform()) {
          try {
            await StatusBar.setOverlaysWebView({ overlay: false });
            await StatusBar.setBackgroundColor({ color: '#ffffff' });
            await StatusBar.setStyle({ style: Style.Light });
          } catch (err) {
            console.warn('StatusBar adjustment before external navigation failed', err);
          }
        }
        window.location.assign(href);
      })();
      return null;
    }
    return originalOpen(url, target, features);
  };
}

keepPaymentFlowInApp();

function App() {
  const queryClient = new QueryClient();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let handle: { remove: () => void } | undefined;
    CapApp.addListener('backButton', ({ canGoBack }) => {
      // Edge-swipe / hardware back: actually navigate history
      if (canGoBack || window.history.length > 1) {
        window.history.back();
      } else {
        CapApp.exitApp();
      }
    }).then((h) => {
      handle = h;
    });

    return () => {
      handle?.remove();
    };
  }, []);

  // Android gets edge-swipe-back for free from the OS's own gesture navigation
  // (it surfaces through the 'backButton' listener above, from either screen
  // edge). iOS has no equivalent here: that gesture is normally provided by
  // UINavigationController's interactive pop, which requires a native nav
  // stack - this app is a single WKWebView with a plain BrowserRouter, so
  // there's no stack for iOS to hook into. This recreates the gesture in JS,
  // recognizing a quick edge-originated swipe from either side and mapping it
  // to the same "go back" action.
  useEffect(() => {
    if (Capacitor.getPlatform() !== 'ios') return;

    const EDGE_ZONE_PX = 24;
    const MIN_SWIPE_PX = 60;
    const MAX_VERTICAL_DRIFT_PX = 60;
    const MAX_DURATION_MS = 600;

    let startX = 0;
    let startY = 0;
    let startTime = 0;
    let startEdge: 'left' | 'right' | null = null;

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) {
        startEdge = null;
        return;
      }
      const touch = e.touches[0];
      startX = touch.clientX;
      startY = touch.clientY;
      startTime = Date.now();
      if (startX <= EDGE_ZONE_PX) {
        startEdge = 'left';
      } else if (startX >= window.innerWidth - EDGE_ZONE_PX) {
        startEdge = 'right';
      } else {
        startEdge = null;
      }
    };

    const onTouchEnd = (e: TouchEvent) => {
      if (!startEdge) return;
      const touch = e.changedTouches[0];
      if (!touch) return;
      const deltaX = touch.clientX - startX;
      const deltaY = touch.clientY - startY;
      const duration = Date.now() - startTime;
      const swipedFromLeftInward = startEdge === 'left' && deltaX > MIN_SWIPE_PX;
      const swipedFromRightInward = startEdge === 'right' && deltaX < -MIN_SWIPE_PX;
      startEdge = null;
      if (
        (swipedFromLeftInward || swipedFromRightInward) &&
        Math.abs(deltaY) < MAX_VERTICAL_DRIFT_PX &&
        duration < MAX_DURATION_MS &&
        window.history.length > 1
      ) {
        window.history.back();
      }
    };

    // passive: true - this only reads touch coordinates, never calls
    // preventDefault, so it can't block scrolling or any other gesture.
    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchend', onTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchend', onTouchEnd);
    };
  }, []);

  // After payment capture redirects to https://localhost/bookings?payment=success
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') === 'success' || params.get('payment') === 'failed') {
      // Clean query so refresh doesn't re-trigger; keep path (e.g. /bookings)
      const clean = window.location.pathname + (window.location.hash || '');
      window.history.replaceState({}, '', clean);
    }
  }, []);

  return (
    <BrowserRouter>
      <IonApp>
        <NativeLaunchAnimation />
        <QueryClientProvider client={queryClient}>
          <React.Suspense fallback={<AppBootLoader />}>
            <FileUploadContext>
              <AppContextProvider>
                <AuthContextProvider>
                  <SearchContextProvider>
                    <EventContextProvider>
                      <LocalizationProvider dateAdapter={AdapterMoment}>
                        <FormContextProvider>
                          <AppSnackBar>
                            <GlobalThemeProvider>
                              <I18nextProvider i18n={i18next}>
                              <ErrorBoundary
                                FallbackComponent={ErrorFallback}
                                onReset={() => {
                                  window.location.reload();
                                }}
                              >
                                <div className="App">
                                  <MainLayout />
                                </div>
                              </ErrorBoundary>
                            </I18nextProvider>
                          </GlobalThemeProvider>
                        </AppSnackBar>
                      </FormContextProvider>
                    </LocalizationProvider>
                  </EventContextProvider>
                </SearchContextProvider>
              </AuthContextProvider>
            </AppContextProvider>
            </FileUploadContext>
          </React.Suspense>
        </QueryClientProvider>
      </IonApp>
    </BrowserRouter>
  );
}

export default App;
